import React from 'react';

import { Link } from '~/lib/react-router-dom';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Accordion, AccordionDetails, AccordionSummary, Button, Chip, Stack, Typography } from '@mui/material';

import { MessageSenders } from '~/consts/supportAssistant';

import { Chat } from '../types/Chat';
import { ChatMessage } from '../types/ChatMessage';

export type AgentMetadataLike = {
  answerSignals?: {
    askedToConnectAgent?: boolean;
    mentionedSupportNumber?: boolean;
    unableToAnswer?: boolean;
  };
  assistantVersion?: string;
  calls?: Array<AgentCallLike>;
  condensedQuery?: string;
  finalAnswerSource?: 'intent' | 'rag' | 'fallback';
  guardReason?: string;
  rag?: {
    articles?: Array<AgentArticleReferenceLike>;
    articleIds?: Array<string>;
    hasRelevantSources?: boolean;
    retrievalQuery?: string;
    steps?: Array<AgentStepLike>;
  };
};

type AgentCallLike = {
  error?: {
    message?: string;
    name?: string;
  };
  functionId?: string;
  latencyMs?: number;
  model?: {
    name?: string;
    provider?: string;
  };
  operation?: string;
  success?: boolean;
  totalUsage?: TokenUsageLike;
  usage?: TokenUsageLike;
};

type AgentStepLike = {
  finishReason?: string;
  stepNumber?: number;
  toolCalls?: Array<{
    toolCallId?: string;
    toolName?: string;
  }>;
  toolResults?: Array<AgentToolResultLike>;
  usage?: TokenUsageLike;
};

type AgentToolResultLike = {
  articles?: Array<AgentArticleReferenceLike>;
  articleIds?: Array<string>;
  hasRelevantSources?: boolean;
  intentProposal?: {
    accepted?: boolean;
    confidence?: number;
    intentKey?: string;
    rejectionReason?: string;
  };
  success?: boolean;
  toolCallId?: string;
  toolName?: string;
};

type AgentArticleReferenceLike = {
  articleId: string;
  distance?: number;
  title?: string;
  url?: string | null;
};

type TokenUsageLike = {
  inputTokens?: number;
  outputTokens?: number;
  totalTokens?: number;
};

type ReasoningRow = {
  articleRefs?: Array<AgentArticleReferenceLike>;
  articleIds?: Array<string>;
  call?: AgentCallLike;
  details?: Array<string>;
  id: string;
  isFinal?: boolean;
  rejectionReason?: string;
  summary: string;
  toolCallIds?: Array<string>;
};

type Props = {
  agentMetadata: AgentMetadataLike;
  chat: Chat;
  message: ChatMessage;
};

const compactReason = (reason?: string) => {
  const reasons: Record<string, string> = {
    ambiguous: 'the request was ambiguous',
    ineligible_booking_intent: 'the customer had no eligible booking',
    low_confidence: 'confidence was too low',
    lower_confidence: 'another intent had higher confidence',
    should_detect_intent_disabled: 'intent detection was disabled',
    superseded_by_sales_sub_intent: 'a more specific sales intent was available',
    top_proposal_unactionable: 'the top intent could not be actioned',
    unmapped_action: 'no chat action is mapped',
  };

  return reason ? (reasons[reason] ?? reason.replace(/_/g, ' ')) : undefined;
};

const compactGuardReason = (reason?: string) => {
  const reasons: Record<string, string> = {
    no_relevant_sources: 'no relevant knowledge sources were found',
    rag_agent_loop_failed: 'the RAG agent failed and used the safe fallback',
    unable_to_answer_signal: 'the answer model said it could not answer',
  };

  return reason ? (reasons[reason] ?? reason.replace(/_/g, ' ')) : undefined;
};

const formatConfidence = (confidence?: number) => {
  return typeof confidence === 'number' ? `${Math.round(confidence * 100)}%` : 'unknown';
};

const formatTokens = (usage?: TokenUsageLike) => {
  if (!usage) {
    return 'n/a';
  }

  const total = usage.totalTokens ?? (usage.inputTokens ?? 0) + (usage.outputTokens ?? 0);

  return `${total} total (${usage.inputTokens ?? 0} in, ${usage.outputTokens ?? 0} out)`;
};

const getAllToolResults = (agentMetadata: AgentMetadataLike) => {
  return agentMetadata.rag?.steps?.flatMap((step) => step.toolResults ?? []) ?? [];
};

const normalizeText = (value?: string) => value?.trim().replace(/\s+/g, ' ').toLowerCase();

const callLabel = (functionId?: string, operation?: string) => {
  const labels: Record<string, string> = {
    'svc-support.agenticAssistant.condenseQuery': 'condense',
    'svc-support.agenticAssistant.intent': 'intent',
    'svc-support.agenticAssistant.knowledgeEmbedding': 'embed',
    'svc-support.agenticAssistant.rag': 'rag',
  };

  if (!functionId) {
    return operation ?? 'call';
  }

  const parts = functionId.split('.');
  return labels[functionId] ?? parts[parts.length - 1] ?? functionId;
};

const formatSuccess = (success?: boolean) => {
  if (typeof success !== 'boolean') {
    return 'unknown';
  }

  return success ? 'succeeded' : 'failed';
};

const getAllToolCallIds = (agentMetadata: AgentMetadataLike, toolName?: string) => {
  const stepToolCallIds =
    agentMetadata.rag?.steps?.flatMap((step) =>
      (step.toolCalls ?? [])
        .filter((toolCall) => !toolName || toolCall.toolName === toolName)
        .map((toolCall) => toolCall.toolCallId),
    ) ?? [];

  const resultToolCallIds = getAllToolResults(agentMetadata)
    .filter((toolResult) => !toolName || toolResult.toolName === toolName)
    .map((toolResult) => toolResult.toolCallId);

  return Array.from(
    new Set(
      [...stepToolCallIds, ...resultToolCallIds].filter((toolCallId): toolCallId is string => Boolean(toolCallId)),
    ),
  );
};

const getArticleIds = (agentMetadata: AgentMetadataLike) => {
  const resultArticleIds = getAllToolResults(agentMetadata).flatMap((toolResult) => toolResult.articleIds ?? []);

  return Array.from(
    new Set(
      [...(agentMetadata.rag?.articleIds ?? []), ...resultArticleIds].filter((articleId): articleId is string =>
        Boolean(articleId),
      ),
    ),
  );
};

const getArticleRefs = (agentMetadata: AgentMetadataLike) => {
  const resultArticleRefs = getAllToolResults(agentMetadata).flatMap((toolResult) => toolResult.articles ?? []);
  const byId = new Map<string, AgentArticleReferenceLike>();

  for (const article of [...(agentMetadata.rag?.articles ?? []), ...resultArticleRefs]) {
    byId.set(article.articleId, { ...byId.get(article.articleId), ...article });
  }

  for (const articleId of getArticleIds(agentMetadata)) {
    if (!byId.has(articleId)) {
      byId.set(articleId, { articleId });
    }
  }

  return Array.from(byId.values());
};

const getCall = (agentMetadata: AgentMetadataLike, functionId: string) => {
  return agentMetadata.calls?.find((call) => call.functionId === functionId);
};

const buildReasoningRows = (
  agentMetadata: AgentMetadataLike,
  answerText?: string,
  originalUserMessage?: string,
): Array<ReasoningRow> => {
  const toolResults = getAllToolResults(agentMetadata);
  const intentResults = toolResults.filter((toolResult) => toolResult.intentProposal?.intentKey);
  const articleIds = getArticleIds(agentMetadata);
  const articleRefs = getArticleRefs(agentMetadata);
  const supportNumberText = /1300\s*489\s*055/.test(answerText ?? '') ? '; answer included support number' : '';
  const finalAnswerSource = agentMetadata.finalAnswerSource;
  const acceptedIntentIndex = intentResults.findIndex((toolResult) => toolResult.intentProposal?.accepted);
  const finalIntentIndex = acceptedIntentIndex >= 0 ? acceptedIntentIndex : 0;
  const rows: Array<ReasoningRow> = [];
  const callSequence = agentMetadata.calls?.map((call) => callLabel(call.functionId, call.operation)).join(' -> ');
  const condensedQueryChanged =
    !!agentMetadata.condensedQuery &&
    (!originalUserMessage || normalizeText(agentMetadata.condensedQuery) !== normalizeText(originalUserMessage));

  if (callSequence) {
    rows.push({
      details: agentMetadata.calls?.map(
        (call) => `${callLabel(call.functionId, call.operation)}: ${formatSuccess(call.success)}`,
      ),
      id: 'call-sequence',
      summary: `Call sequence: ${callSequence}.`,
    });
  }

  if (condensedQueryChanged) {
    rows.push({
      call: getCall(agentMetadata, 'svc-support.agenticAssistant.condenseQuery'),
      details: [`Condensed query: ${agentMetadata.condensedQuery}`],
      id: 'condensed-query',
      summary: `Condensed query: ${agentMetadata.condensedQuery}`,
    });
  }

  rows.push(
    ...intentResults.map((toolResult, index) => {
      const proposal = toolResult.intentProposal;
      const isFinalIntent = finalAnswerSource === 'intent' && index === finalIntentIndex;
      const wasAccepted = !!proposal?.accepted || isFinalIntent;
      const intentKey = proposal?.intentKey ?? 'unknown intent';
      const confidence = formatConfidence(proposal?.confidence);
      const rejectionReason = compactReason(proposal?.rejectionReason);
      const rejectionText = !wasAccepted && rejectionReason ? ` because ${rejectionReason}` : '';
      const outcome = wasAccepted ? `accepted ${intentKey}` : `proposed ${intentKey}, but rejected${rejectionText}`;

      return {
        call: getCall(agentMetadata, 'svc-support.agenticAssistant.intent'),
        id: `intent-${index}`,
        isFinal: isFinalIntent,
        rejectionReason,
        details: [
          `Tool result: ${formatSuccess(toolResult.success)}`,
          `Status: ${wasAccepted ? 'accepted' : 'rejected'}`,
        ],
        summary: `Checked intent: ${outcome} with ${confidence} confidence.`,
        toolCallIds: toolResult.toolCallId ? [toolResult.toolCallId] : [],
      };
    }),
  );

  const knowledgeToolResults = toolResults.filter((toolResult) => toolResult.toolName === 'searchKnowledge');
  const hasRelevantSources =
    agentMetadata.rag?.hasRelevantSources ??
    knowledgeToolResults.find((toolResult) => typeof toolResult.hasRelevantSources === 'boolean')?.hasRelevantSources;

  if (knowledgeToolResults.length || articleIds.length) {
    rows.push({
      articleRefs,
      articleIds,
      call: getCall(agentMetadata, 'svc-support.agenticAssistant.knowledgeEmbedding'),
      details: [
        `Tool result: ${knowledgeToolResults.some((toolResult) => !toolResult.success) ? 'failed' : 'succeeded'}`,
        `hasRelevantSources: ${typeof hasRelevantSources === 'boolean' ? String(hasRelevantSources) : 'n/a'}`,
        ...(agentMetadata.rag?.retrievalQuery ? [`Retrieval query: ${agentMetadata.rag.retrievalQuery}`] : []),
      ],
      id: 'knowledge',
      summary: `Searched knowledge base: found ${articleIds.length} ${articleIds.length === 1 ? 'article' : 'articles'}; hasRelevantSources: ${
        typeof hasRelevantSources === 'boolean' ? String(hasRelevantSources) : 'n/a'
      }.`,
      toolCallIds: getAllToolCallIds(agentMetadata, 'searchKnowledge'),
    });
  }

  const ragCall = getCall(agentMetadata, 'svc-support.agenticAssistant.rag');

  if (ragCall || agentMetadata.rag?.steps?.length) {
    rows.push({
      articleRefs,
      articleIds,
      call: ragCall,
      id: 'answer',
      isFinal: finalAnswerSource === 'rag' || (!finalAnswerSource && !!ragCall),
      details: agentMetadata.answerSignals
        ? [
            `unableToAnswer: ${String(agentMetadata.answerSignals.unableToAnswer)}`,
            `askedToConnectAgent: ${String(agentMetadata.answerSignals.askedToConnectAgent)}`,
            `mentionedSupportNumber: ${String(agentMetadata.answerSignals.mentionedSupportNumber)}`,
          ]
        : [],
      summary: articleIds.length
        ? `Generated answer from ${articleIds.length} retrieved ${articleIds.length === 1 ? 'article' : 'articles'}${supportNumberText}.`
        : `Generated answer without retrieved articles${supportNumberText}.`,
      toolCallIds: getAllToolCallIds(agentMetadata),
    });
  }

  const acceptedIntent =
    finalAnswerSource === 'intent' || intentResults.some((toolResult) => toolResult.intentProposal?.accepted);
  let finalSummary = 'Final outcome: assistant answer shown.';

  if (agentMetadata.finalAnswerSource === 'fallback') {
    finalSummary = 'Final outcome: fallback answer shown.';
  } else if (ragCall) {
    finalSummary = 'Final outcome: RAG answer shown.';
  }

  if (acceptedIntent) {
    finalSummary = 'Final outcome: intent response shown.';
  }

  rows.push({
    call: ragCall ?? getCall(agentMetadata, 'svc-support.agenticAssistant.intent'),
    details: [
      ...(agentMetadata.guardReason ? [`Guard reason: ${compactGuardReason(agentMetadata.guardReason)}`] : []),
      ...(agentMetadata.answerSignals
        ? [
            `unableToAnswer: ${String(agentMetadata.answerSignals.unableToAnswer)}`,
            `askedToConnectAgent: ${String(agentMetadata.answerSignals.askedToConnectAgent)}`,
            `mentionedSupportNumber: ${String(agentMetadata.answerSignals.mentionedSupportNumber)}`,
          ]
        : []),
    ],
    id: 'outcome',
    isFinal: finalAnswerSource === 'fallback',
    summary: finalSummary,
  });

  return rows;
};

const getOriginalUserMessage = (chat: Chat, message: ChatMessage) => {
  const messages = (chat as Chat & { messages?: Array<ChatMessage> }).messages ?? [];
  const currentIndex = messages.findIndex((chatMessage) => chatMessage.id === message.id);
  const previousMessages = currentIndex >= 0 ? messages.slice(0, currentIndex) : messages;
  const previousUserMessage = [...previousMessages]
    .reverse()
    .find((chatMessage) => chatMessage.sender === MessageSenders.USER);

  return previousUserMessage?.text;
};

const renderReasoningDetails = (row: ReasoningRow, chat: Chat, message: ChatMessage) => {
  const { articleIds, articleRefs, call, details, rejectionReason, toolCallIds } = row;
  const hasFailed = typeof call?.success === 'boolean' && !call.success;

  return (
    <Stack spacing={0.5}>
      {details?.map((detail) => (
        <Typography key={detail} variant="caption">
          {detail}
        </Typography>
      ))}
      <Typography variant="caption">Model: {call?.model?.name ?? call?.operation ?? 'n/a'}</Typography>
      <Typography variant="caption">
        Latency: {typeof call?.latencyMs === 'number' ? `${call.latencyMs}ms` : 'n/a'}
      </Typography>
      <Typography variant="caption">Tokens: {formatTokens(call?.totalUsage ?? call?.usage)}</Typography>
      {!!toolCallIds?.length && <Typography variant="caption">Tool call IDs: {toolCallIds.join(', ')}</Typography>}
      {!!articleIds?.length && (
        <Stack spacing={0.25}>
          <Typography variant="caption">Articles:</Typography>
          {articleRefs?.map((article) => (
            <Stack key={article.articleId} direction="row" spacing={0.5} alignItems="baseline">
              {article.url ? (
                <Button href={article.url} target="_blank" variant="text" size="small" sx={{ p: 0, minWidth: 'auto' }}>
                  {article.title ?? article.articleId}
                </Button>
              ) : (
                <Typography variant="caption">{article.title ?? article.articleId}</Typography>
              )}
              {typeof article.distance === 'number' && (
                <Typography variant="caption">({article.distance.toFixed(3)})</Typography>
              )}
            </Stack>
          ))}
          <Button
            to={`/support-assistant/articles?chatId=${chat.id}&messageId=${message.id}&articleIds=${articleIds.join(',')}`}
            component={Link}
            variant="text"
            size="small"
            sx={{ alignSelf: 'flex-start', p: 0, minWidth: 'auto' }}
          >
            Open article details
          </Button>
        </Stack>
      )}
      {rejectionReason && <Typography variant="caption">Rejection reason: {rejectionReason}</Typography>}
      {hasFailed && (
        <Typography variant="caption" color="error">
          Error: {call?.error?.message ?? 'call failed'}
        </Typography>
      )}
    </Stack>
  );
};

export default function AgentReasoning({ chat, message, agentMetadata }: Props) {
  const rows = buildReasoningRows(agentMetadata, message.text, getOriginalUserMessage(chat, message));

  if (!rows.length) {
    return null;
  }

  return (
    <Accordion
      disableGutters
      elevation={0}
      sx={{
        bgcolor: 'transparent',
        borderTop: '1px solid',
        borderColor: 'divider',
        boxShadow: 'none',
        mt: 0.5,
        '&:before': { display: 'none' },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon fontSize="small" />}
        sx={{
          minHeight: 30,
          px: 0,
          '& .MuiAccordionSummary-content': { alignItems: 'center', my: 0.5 },
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="caption" fontWeight={600}>
            Show agent reasoning
          </Typography>
          {agentMetadata.assistantVersion && (
            <Chip size="small" label={agentMetadata.assistantVersion} variant="outlined" />
          )}
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ px: 0, py: 0.5 }}>
        <Stack spacing={0.5}>
          {rows.map((row) => (
            <Accordion
              key={row.id}
              disableGutters
              elevation={0}
              sx={{
                bgcolor: row.isFinal ? 'success.light' : 'transparent',
                border: '1px solid',
                borderColor: row.isFinal ? 'success.main' : 'divider',
                borderRadius: '6px',
                '&:before': { display: 'none' },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon fontSize="small" />}
                sx={{
                  minHeight: 32,
                  px: 1,
                  '& .MuiAccordionSummary-content': { my: 0.5 },
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center">
                  {row.isFinal && <Chip size="small" color="success" label="Final answer" />}
                  <Typography variant="caption">{row.summary}</Typography>
                </Stack>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 1, py: 0.75 }}>{renderReasoningDetails(row, chat, message)}</AccordionDetails>
            </Accordion>
          ))}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}
